import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { extractStreetAddress } from '../lib/campUtils';
import { track } from '../lib/track';

interface CalendarExportProps {
  childName: string;
  campName: string;
  startDate: string;
  endDate: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

export default function CalendarExport({ 
  childName, 
  campName, 
  startDate, 
  endDate,
  address,
  city,
  state,
  zipCode
}: CalendarExportProps) {
  const generateICSFile = () => {
    // Add debug logging
    console.log('Calendar Export Props:', {
      address,
      city,
      state,
      zipCode
    });

    const formatAddress = () => {
      // Extract just the street address and combine with separate city/state/zip
      if (address || city || state || zipCode) {
        const streetAddress = extractStreetAddress(address || null, city || null, state || null, zipCode || null);
        const locationParts = [city, state, zipCode].filter(Boolean);
        
        const addressParts = [
          streetAddress,
          locationParts.join(' ')
        ].filter(Boolean);
        
        const formattedAddress = addressParts.join(', ');
        console.log('Formatted Address:', formattedAddress);
        // Escape special characters and replace commas with \\,
        return formattedAddress.replace(/[,\n]/g, '\\$&');
      }
      
      return 'Location TBD';
    };

    // Use DATE format for all-day events (same as calendar feed)
    const startDateObj = new Date(startDate + 'T00:00:00');
    const endDateObj = new Date(endDate + 'T00:00:00');
    const endDateExclusive = new Date(endDateObj);
    endDateExclusive.setDate(endDateExclusive.getDate() + 1); // Add one day for exclusive DTEND

    const formatICSDateOnly = (date: Date): string => {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    };

    const escapeICS = (text: string): string => {
      return text
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\n/g, '\\n')
        .replace(/\r/g, '');
    };

    const now = new Date();
    const eventUid = `campwhiz-${Date.now()}-${Math.random().toString(36).substr(2, 9)}@campwhiz.com`;
    const summary = `${campName} - ${childName}`;
    const description = `Summer camp schedule for ${childName} at ${campName}`;

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//CampWhiz//Single Camp Export//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
X-WR-CALNAME:CampWhiz Camp Schedule
X-WR-TIMEZONE:America/New_York
BEGIN:VEVENT
UID:${eventUid}
DTSTAMP:${now.toISOString().replace(/[-:]/g, '').split('.')[0]}Z
DTSTART;VALUE=DATE:${formatICSDateOnly(startDateObj)}
DTEND;VALUE=DATE:${formatICSDateOnly(endDateExclusive)}
SUMMARY:${escapeICS(summary)}
DESCRIPTION:${escapeICS(description)}
LOCATION:${escapeICS(formatAddress())}
SEQUENCE:0
STATUS:CONFIRMED
END:VEVENT
END:VCALENDAR`;

    // Log the final ICS content
    console.log('ICS Content:', icsContent);

    track('calendar_ics_downloaded', {
      persona: 'parent',
      scope: 'single',
      child_name: childName,
      camp_name: campName,
    });

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `${campName}-${childName}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <button
      onClick={generateICSFile}
      className="text-sm text-primary hover:text-primary/80 flex items-center gap-1 transition-colors"
    >
      <ArrowDownTrayIcon className="h-4 w-4" />
      Download Calendar File (.ICS)
    </button>
  );
}